/**
 * CleosInfo.js
 * ============
 * Floating widget that polls the backend every 2 seconds to display
 * live blockchain info parsed from the nodeos log on the server.
 *
 * Fields shown: head_block_num, head_block_producer
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import config from './blockexplorer-config.json';
import './CleosInfo.css';

const POLL_INTERVAL = 5000; // 5 seconds

const CleosInfo = () => {
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [collapsed, setCollapsed] = useState(false);
    const intervalRef = useRef(null);

    // Drag state
    const [position, setPosition] = useState({ right: 24, bottom: 24 });
    const dragging = useRef(false);
    const dragOffset = useRef({ x: 0, y: 0 });
    const widgetRef = useRef(null);

    const onPointerDown = useCallback((e) => {
        // Only drag on left button
        if (e.button !== 0) return;
        dragging.current = true;
        const rect = widgetRef.current.getBoundingClientRect();
        dragOffset.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        e.currentTarget.setPointerCapture(e.pointerId);
        e.preventDefault();
    }, []);

    const onPointerMove = useCallback((e) => {
        if (!dragging.current) return;
        const x = e.clientX - dragOffset.current.x;
        const y = e.clientY - dragOffset.current.y;
        setPosition({ left: x, top: y, right: 'auto', bottom: 'auto' });
    }, []);

    const onPointerUp = useCallback((e) => {
        if (!dragging.current) return;
        dragging.current = false;
        e.currentTarget.releasePointerCapture(e.pointerId);
    }, []);

    const fetchInfo = async () => {
        try {
            const response = await fetch(`${config.fallbackEndpoint}/cleos-info`);
            if (!response.ok) {
                const body = await response.json();
                throw new Error(body.error || `HTTP ${response.status}`);
            }
            const data = await response.json();
            setInfo(data);
            setError(null);
            setLastUpdated(new Date());
        } catch (err) {
            setError(err.message);
        }
    };

    useEffect(() => {
        fetchInfo();
        intervalRef.current = setInterval(fetchInfo, POLL_INTERVAL);
        return () => clearInterval(intervalRef.current);
    }, []);

    return (
        <div
            ref={widgetRef}
            className={`cleos-widget${collapsed ? ' cleos-widget--collapsed' : ''}`}
            style={position}
        >
            <div
                className="cleos-widget__header"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onClick={() => setCollapsed(c => !c)}
            >
                <span className={`cleos-pulse${error ? ' cleos-pulse--error' : ''}`} />
                <span className="cleos-widget__title">Node Status</span>
                <span className="cleos-widget__toggle">{collapsed ? '▲' : '▼'}</span>
            </div>

            {!collapsed && (
                <div className="cleos-widget__body">
                    {error ? (
                        <div className="cleos-row cleos-error">
                            <i className="pi pi-exclamation-triangle" />
                            <span>{error}</span>
                        </div>
                    ) : info ? (
                        <>
                            <div className="cleos-row">
                                <span className="cleos-label">Block #</span>
                                <span className="cleos-value cleos-value--highlight">
                                    {info.head_block_num}
                                </span>
                            </div>
                            <div className="cleos-row">
                                <span className="cleos-label">Producer</span>
                                <span className="cleos-value">{info.head_block_producer}</span>
                            </div>
                        </>
                    ) : (
                        <div className="cleos-row cleos-loading">
                            <span>Fetching…</span>
                        </div>
                    )}

                    {lastUpdated && (
                        <div className="cleos-footer">
                            updated {lastUpdated.toLocaleTimeString()}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default CleosInfo;
